// Field Survey map style (patterns/maps.md): the Protomaps basemap, recoloured from our `map.*`
// tokens, with the sprite-based layers (POI icons, road shields, one-way arrows) removed so the
// style needs no sprite. Stations keep a text-only label: they're how you find your way in a city.
// CJK ideographs, kana and Hangul render from the device's fonts (MapLibre's local ideographs),
// so the bundled glyph ranges below cover everything else.
import type { LayerSpecification, StyleSpecification } from '@maplibre/maplibre-gl-style-spec';
import { type Flavor, layers, namedFlavor } from '@protomaps/basemaps';

export const MODES = ['light', 'dark', 'lightHighContrast', 'darkHighContrast'] as const;
export type MapMode = (typeof MODES)[number];

export type Palette = {
  land: string;
  water: string;
  park: string;
  building: string;
  buildingEdge: string;
  road: string;
  roadMinor: string;
  roadCasing: string;
  rail: string;
  path: string;
  label: string;
  labelHalo: string;
  /** `day.1`…`day.8`, for routes and pins drawn on top. */
  days: string[];
  surface: string;
  ink: string;
  signal: string;
};

export const FONTS = ['Noto Sans Regular', 'Noto Sans Medium', 'Noto Sans Italic'] as const;

/** Glyph ranges bundled for offline use (first code point of each 256-glyph range). */
export const GLYPH_RANGES = [
  ...[0, 256, 512, 768, 1024], // Latin, Latin Extended, IPA, Greek, Cyrillic
  3584, // Thai
  7680,
  7936, // Latin Extended Additional (Vietnamese), Greek Extended
  8192,
  8448,
  8704,
  8960, // punctuation, currency (₱ € ¥ ₩), letterlike, arrows, maths, technical
  9472,
  9728, // box drawing, geometric shapes, misc symbols
  12288, // CJK symbols and punctuation, kana punctuation
  65280, // fullwidth forms
];

/** Fonts for CJK text, from the device (MapLibre `localIdeographFontFamily`). */
export const LOCAL_IDEOGRAPH_FONTS =
  '"Hiragino Sans", "Apple SD Gothic Neo", "Source Han Sans JP", "Noto Sans CJK JP", sans-serif';

export const ATTRIBUTION = '© OpenStreetMap contributors · Protomaps';

const DROPPED = new Set(['pois', 'roads_shields', 'roads_oneway', 'address_label']);

function flavor(p: Palette): Flavor {
  const base = namedFlavor('light');
  const land = p.land;
  return {
    ...base,
    background: land,
    earth: land,
    park_a: p.park,
    park_b: p.park,
    wood_a: p.park,
    wood_b: p.park,
    scrub_a: p.park,
    scrub_b: p.park,
    zoo: p.park,
    hospital: land,
    industrial: land,
    school: land,
    military: land,
    aerodrome: land,
    pedestrian: land,
    beach: land,
    sand: land,
    glacier: land,
    runway: p.roadMinor,
    water: p.water,
    buildings: p.building,
    pier: p.roadMinor,
    other: p.path,
    minor_service: p.roadMinor,
    minor_a: p.roadMinor,
    minor_b: p.roadMinor,
    link: p.road,
    major: p.road,
    highway: p.road,
    tunnel_other: p.roadMinor,
    tunnel_minor: p.roadMinor,
    tunnel_link: p.roadMinor,
    tunnel_major: p.roadMinor,
    tunnel_highway: p.roadMinor,
    bridges_other: p.path,
    bridges_minor: p.roadMinor,
    bridges_link: p.road,
    bridges_major: p.road,
    bridges_highway: p.road,
    tunnel_other_casing: p.roadCasing,
    tunnel_minor_casing: p.roadCasing,
    tunnel_link_casing: p.roadCasing,
    tunnel_major_casing: p.roadCasing,
    tunnel_highway_casing: p.roadCasing,
    minor_service_casing: p.roadCasing,
    minor_casing: p.roadCasing,
    link_casing: p.roadCasing,
    major_casing_late: p.roadCasing,
    highway_casing_late: p.roadCasing,
    major_casing_early: p.roadCasing,
    highway_casing_early: p.roadCasing,
    bridges_other_casing: p.roadCasing,
    bridges_minor_casing: p.roadCasing,
    bridges_link_casing: p.roadCasing,
    bridges_major_casing: p.roadCasing,
    bridges_highway_casing: p.roadCasing,
    railway: p.rail,
    boundaries: p.roadCasing,
    roads_label_minor: p.label,
    roads_label_minor_halo: p.labelHalo,
    roads_label_major: p.label,
    roads_label_major_halo: p.labelHalo,
    ocean_label: p.label,
    subplace_label: p.label,
    subplace_label_halo: p.labelHalo,
    city_label: p.label,
    city_label_halo: p.labelHalo,
    state_label: p.label,
    state_label_halo: p.labelHalo,
    country_label: p.label,
    address_label: p.label,
    address_label_halo: p.labelHalo,
    regular: 'Noto Sans Regular',
    bold: 'Noto Sans Medium',
    italic: 'Noto Sans Italic',
  };
}

function recolour(layer: LayerSpecification, p: Palette): LayerSpecification {
  if (layer.id === 'buildings' && layer.type === 'fill') {
    return {
      ...layer,
      paint: {
        ...layer.paint,
        'fill-color': p.building,
        'fill-outline-color': p.buildingEdge,
        'fill-opacity': 1,
      },
    };
  }
  if (layer.type === 'symbol') {
    // No sprite: drop icon properties (e.g. the town dots on locality labels); the text stays.
    const noIcons = (o: object | undefined) =>
      Object.fromEntries(Object.entries(o ?? {}).filter(([k]) => !k.startsWith('icon-')));
    const paint = {
      ...noIcons(layer.paint),
      'text-color': p.label,
      'text-halo-color': p.labelHalo,
      'text-halo-width': 1.5,
    };
    layer = { ...layer, layout: noIcons(layer.layout) };
    // Neighbourhoods read as field labels: caps with +8% tracking (patterns/maps.md, Typography).
    if (layer.id === 'places_subplace') {
      return {
        ...layer,
        layout: { ...layer.layout, 'text-transform': 'uppercase', 'text-letter-spacing': 0.08 },
        paint,
      };
    }
    return { ...layer, paint };
  }
  return layer;
}

/** Station names from the POI layer, as text only (no sprite). */
function stations(poi: LayerSpecification | undefined, p: Palette): LayerSpecification[] {
  if (poi?.type !== 'symbol') return [];
  const {
    'icon-image': _icon,
    'text-offset': _offset,
    'text-variable-anchor': _anchor,
    'text-radial-offset': _radial,
    ...layout
  } = poi.layout ?? {};
  return [
    {
      ...poi,
      id: 'stations',
      minzoom: 13,
      filter: ['in', ['get', 'kind'], ['literal', ['station', 'ferry_terminal']]],
      layout: { ...layout, 'text-font': ['Noto Sans Medium'], 'text-size': 11, 'text-anchor': 'center' },
      paint: { 'text-color': p.label, 'text-halo-color': p.labelHalo, 'text-halo-width': 1.5 },
    },
  ];
}

export function fieldSurveyStyle(o: {
  palette: Palette;
  /** `pmtiles://https://…/maps/kyoto.pmtiles`, or a local file URL in the apps. */
  tiles: string;
  /** `…/{fontstack}/{range}.pbf` */
  glyphs: string;
  lang?: string;
}): StyleSpecification {
  const generated = layers('protomaps', flavor(o.palette), { lang: o.lang ?? 'en' });
  const kept = generated.filter((l) => !DROPPED.has(l.id)).map((l) => recolour(l, o.palette));
  // Stations sit just under the place labels, so towns and neighbourhoods win collisions.
  const placesAt = kept.findIndex((l) => l.id.startsWith('places_'));
  kept.splice(
    placesAt,
    0,
    ...stations(
      generated.find((l) => l.id === 'pois'),
      o.palette,
    ),
  );
  return {
    version: 8,
    glyphs: o.glyphs,
    sources: { protomaps: { type: 'vector', url: o.tiles, attribution: ATTRIBUTION } },
    layers: kept,
  };
}
